import { Injectable, inject, signal } from '@angular/core';
import { LicenseType } from './gqty/schema.generated';
import { SettingsService } from '../sections/settings/settings.service';
import { GATED_MODULES, GatedModuleConfig } from '../sections/gated/gated-modules';

/** Gated upsell shown when a licensed module is inactive. */
const MODULE_UPSELL_KEY: Partial<Record<LicenseType, string>> = {
  [LicenseType.EVERPLAN_LIVE]: 'live',
  [LicenseType.EVERPLAN_CONNECT]: 'connect',
  [LicenseType.EVERPLAN_PLANNING]: 'planning',
  [LicenseType.EVERPLAN_TRANSPORTATION]: 'transportation',
};

/**
 * App-wide source of truth for the tenant's active module licenses. Loaded once
 * and shared via a signal so the shell, settings and route guards all gate
 * against the same state.
 */
@Injectable({ providedIn: 'root' })
export class LicenseService {
  private readonly settingsService = inject(SettingsService);

  /** Active module license types; empty until {@link load} resolves. */
  readonly activeModules = signal<ReadonlySet<LicenseType>>(new Set());

  private loaded?: Promise<void>;

  /** Loads licenses once (subsequent calls reuse the same promise). */
  load(): Promise<void> {
    return (this.loaded ??= this.fetch());
  }

  private async fetch() {
    try {
      const licenses = await this.settingsService.getLicenses();
      const active = new Set(licenses.filter((l) => l.isActive).map((l) => l.type as LicenseType));
      this.activeModules.set(active);
      if (licenses.length === 0) {
        // An *empty license list* at cold boot is the race signal: auth/licenses
        // weren't available yet. Don't memoize it, so the next navigation retries
        // instead of locking every module (e.g. Live) for the whole session. A
        // tenant that genuinely has licenses returns a non-empty list — even if
        // none is active (expired trial) — and is cached normally, so we don't
        // refetch on every navigation.
        this.loaded = undefined;
      }
    } catch {
      // On failure, don't memoize either — retry on the next call. Modules stay
      // empty for now so gated surfaces stay locked rather than wrongly granting.
      this.loaded = undefined;
    }
  }

  /** Whether the tenant has an active license for the given module. */
  hasModule(module: LicenseType): boolean {
    return this.activeModules().has(module);
  }

  /** The gated upsell to show for a module the tenant doesn't have. */
  moduleUpsell(module: LicenseType): GatedModuleConfig | undefined {
    const key = MODULE_UPSELL_KEY[module];
    if (!key) return undefined;
    // Pin the requested module to the one asked for, so the "contact sales" CTA
    // reports exactly this license regardless of the static config default.
    return { ...GATED_MODULES[key], module };
  }
}
